import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import EditIcon from '@mui/icons-material/ModeOutlined';
import { BASE_URL_API } from '../Constants';
import axios from 'axios';
import '../Components/modal.css';
import './edit.css';
import './details.css';
import DataGridTable from "../Components/DataGridTable";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { GridActionsCellItem } from '@mui/x-data-grid';

import './tabs.css';
import { useAuth } from "../hooks/AuthProvider";
import Spinner from '../Components/Spinner';
import Alert from '../Components/Alert';
import { deleteMultiLocations } from '../lib/actions';

import { LanguageContext } from '../context/LanguageContext';
import locTranslations from '../languages/location.json';

import { getErrorMessage } from '../utils/errorUtils';
import { getDeviceDropdownDid, getDeviceDropdownId } from '../utils/dropdownLabels';
import { normalizeLocationListRow } from '../utils/listRowIds';

import { AddLocation } from './AddLocation';
import { EditLocation } from './EditLocation';
import AppDialog from '../Components/AppDialog'; 

const formatLocationDeviceLabel = (device) => {
    if (!device) return '-';

    const deviceId = Number(device?.id ?? device?._row_id);
    const did = getDeviceDropdownDid(device);
    const name = device?.name || did || 'Device';

    return Number.isFinite(deviceId) && deviceId > 0
        ? `${name} (#${deviceId})`
        : name;
};

const LocationDetailsModal = ({ state, setState }) => {
    const { lang } = useContext(LanguageContext);
    const t = locTranslations[lang];
    const isRtl = ['ar', 'ur'].includes(lang);

    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(state?.location || null);
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right',
    });

    const closeModal = useCallback(() => {
        setState({ open: false, id: 0, location: null });
    }, [setState]);

    const showAlert = useCallback((type, message) => {
        setAlertState({
            open: true,
            message,
            type,
            position: isRtl ? 'left' : 'right',
        });
    }, [isRtl]);

    useEffect(() => {
        if (!state?.open) return;

        setDetails(state?.location || null);

        if (!state?.id) return;

        let mounted = true;
        const fetchLocationDetails = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${BASE_URL_API}/location/${state.id}`);
                if (!mounted) return;
                setDetails(normalizeLocationListRow(res?.data?.data || {}));
            } catch (err) {
                if (!mounted) return;
                showAlert('error', getErrorMessage(err));
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchLocationDetails();

        return () => {
            mounted = false;
        };
    }, [showAlert, state?.id, state?.location, state?.open]);

    if (!state?.open) return null;

    const associatedDevices = Array.isArray(details?.devices) ? details.devices : [];

    return (
        <div id="modal" dir={isRtl ? 'rtl' : 'ltr'}>
            <Alert
                open={alertState.open}
                close={() => setAlertState((current) => ({ ...current, open: false }))}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div id="popup" className="dashboard-modal">
                <div className="dashboard-header">
                    <div className="dashboard-header-text">
                        <h2>{details?.name || t.locationsPageTitle}</h2>
                        <p>{details?.address || t.noData}</p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-content dashboard-content">
                    {loading && !details ? (
                        <Spinner />
                    ) : (
                        <div className="form-section">
                            <div className="form-item">
                                <label className="form-lbl">{t.column.name || 'Name'}</label>
                                <input className="add-column" value={details?.name || ''} readOnly />
                            </div>

                            <div className="form-item">
                                <label className="form-lbl">{t.column.address || 'Address'}</label>
                                <input className="add-column" value={details?.address || ''} readOnly />
                            </div>

                            <div className="form-item">
                                <label className="form-lbl">{t.column.latitude || 'Latitude'}</label>
                                <input className="add-column" value={details?.latitude ?? ''} readOnly />
                            </div>

                            <div className="form-item">
                                <label className="form-lbl">{t.column.longitude || 'Longitude'}</label>
                                <input className="add-column" value={details?.longitude ?? ''} readOnly />
                            </div>

                            <div className="form-item">
                                <label className="form-lbl">{t.popup?.labelDevice || 'Devices'}</label>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 10,
                                    }}
                                >
                                    {associatedDevices.length > 0 ? associatedDevices.map((device) => (
                                        <div
                                            key={device?.id ?? device?._row_id}
                                            className="add-column"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                minHeight: 44,
                                            }}
                                        >
                                            {formatLocationDeviceLabel(device)}
                                        </div>
                                    )) : (
                                        <div className="add-column" style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>
                                            {t.noData}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer dashboard-footer">
                    <div />
                    <div className="modal-footer-right-buttons">
                        <button className="btn btn-cancel" onClick={closeModal}>
                            {t.popup?.buttonClose || 'Close'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const Locations = () => {

    const { permissions, org } = useAuth();
    const canView = permissions['view_locations'];
    const canCreate = permissions['create_locations'];
    const canEdit = permissions['edit_locations'];
    const canDelete = permissions['delete_locations'];
    const canViewDevices = permissions['view_devices'];

    const [rows, setRows] = useState([]);
    const [devices, setDevices] = useState([]);
    const [devicesLoading, setDevicesLoading] = useState(false);
    const { lang } = useContext(LanguageContext);
    const t = locTranslations[lang];
    const isRtl = ['ar', 'ur'].includes(lang);

    const resolverRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: "alert",     // "alert" | "confirm"
        variant: "info",   // "info" | "danger" | "error"
        icon: "alert",     // "alert" | "delete"
        title: "",
        message: "",
        confirmText: "Ok",
        cancelText: "Cancel",
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog((d) => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise((resolve) => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || "alert",
                    variant: opts?.variant || "info",
                    icon: opts?.icon || "alert",
                    title: opts?.title || "",
                    message: opts?.message || "",
                    confirmText: opts?.confirmText || "Ok",
                    cancelText: opts?.cancelText || "Cancel",
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        []
    );

    const showAlert = useCallback(
        async (type, message) => {
            const variant =
                type === "error" ? "error" : type === "warning" ? "info" : "info";

            await alert({
                type: "alert",
                variant,
                icon: "alert",
                title:
                    type === "error"
                        ? (t?.alert?.errorTitle || "Error")
                        : type === "warning"
                            ? (t?.alert?.warningTitle || "Warning")
                            : (t?.alert?.infoTitle || "Info"),
                message: message || "",
                confirmText: "Ok",
            });
        },
        [alert, t?.alert?.errorTitle, t?.alert?.warningTitle, t?.alert?.infoTitle]
    );

    const [loading, setLoading] = useState(true);
    const pagingoptions = [10, 20, 50, 100];

    // modal states
    const [addLocationOpen, setAddLocationOpen] = useState(false);
    const [editLocationOpen, setEditLocationOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [viewingLocation, setViewingLocation] = useState({ open: false, id: 0, location: null });

    const datasource = BASE_URL_API + '/location';
    const fetchDevices = useCallback(async () => {
        if (!canViewDevices) {
            setDevices([]);
            return;
        }

        const devicesDatasource = org?.id
            ? `${BASE_URL_API}/device/filtered/${org.id}`
            : `${BASE_URL_API}/device/list`;

        setDevicesLoading(true);
        try {
            const res = await axios.get(devicesDatasource);
            if (res.data && Array.isArray(res.data.data)) {
                setDevices(res.data.data);
            } else {
                console.error("Received unexpected data format from /device/list:", res.data);
                setDevices([]);
                await showAlert('error', 'Received an invalid format from the server.');
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            await showAlert('error', `Could not load devices: ${errorMessage}`);
            setDevices([]);
        } finally {
            setDevicesLoading(false);
        }
    }, [canViewDevices, org?.id, showAlert]);

    const openAddLocationModal = useCallback(async () => {
        if (!canCreate) {
            await showAlert('error', t.noPermission);
            return;
        }

        await fetchDevices();
        setAddLocationOpen(true);
    }, [canCreate, fetchDevices, showAlert, t.noPermission]);

    const openEditLocationModal = useCallback(async (locationRow) => {
        if (!canEdit) {
            await showAlert('error', t.noPermission);
            return;
        }

        await fetchDevices();
        setEditingLocation(locationRow);
        setEditLocationOpen(true);
    }, [canEdit, fetchDevices, showAlert, t.noPermission]);

    const handleDeleteClick = (id) => async () => {
        if (!canDelete) {
            await showAlert('error', t.noPermission);
            return;
        }

        const confirmed = await alert({
            type: "confirm",
            variant: "danger",
            icon: "delete",
            title: t?.deleteTitle || t?.actions?.delete || "Delete",
            message: t?.confirmDelete || "Are you sure you want to delete?",
            confirmText: t?.confirmText || "Delete",
            cancelText: t?.cancelText || "Cancel",
            showCancel: true,
        });

        if (!confirmed) return;

        const originalRows = rows;
        setRows(currentRows => currentRows.filter(row => row.id !== id));

        try {
            const res = await axios.delete(`${datasource}/${id}`);
            await showAlert('success', res.data.message || 'Location deleted successfully.');
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            await showAlert('error', errorMessage);
            setRows(originalRows);
        }
    };

    const deviceMap = useMemo(() => {
        const map = new Map();
        (devices || []).forEach((device) => {
            const deviceId = Number(getDeviceDropdownId(device));
            if (Number.isFinite(deviceId)) {
                map.set(deviceId, device);
            }
        });
        return map;
    }, [devices]);

    const resolveDevicesLabel = useCallback((row) => {
        if (!row) return '-';

        const inlineDevices = Array.isArray(row.devices) ? row.devices : [];
        const inlineDevicesById = new Map(
            inlineDevices
                .map((device) => [Number(device?.id ?? device?._row_id), device])
                .filter(([deviceId]) => Number.isFinite(deviceId) && deviceId > 0)
        );

        const deviceIds = Array.isArray(row.device_ids)
            ? row.device_ids
            : row.device_id
                ? [row.device_id]
                : [];

        const labels = deviceIds
            .map((rawDeviceId) => {
                const deviceId = Number(rawDeviceId);
                if (!Number.isFinite(deviceId) || deviceId <= 0) return null;

                const inlineDevice = inlineDevicesById.get(deviceId);
                if (inlineDevice) {
                    return formatLocationDeviceLabel(inlineDevice);
                }

                const device = deviceMap.get(deviceId);
                if (device) {
                    const name = device.name || getDeviceDropdownDid(device) || 'Device';
                    return `${name} (#${deviceId})`;
                }

                return `#${deviceId}`;
            })
            .filter(Boolean);

        if (!labels.length) return '-';
        if (labels.length <= 2) return labels.join(', ');
        return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
    }, [deviceMap]);

    const openLocationDetails = useCallback((locationRow) => {
        if (!locationRow) return;

        setViewingLocation({
            open: true,
            id: Number(locationRow?.id),
            location: locationRow,
        });
    }, []);

    const canPerformActions = canView || canEdit || canDelete;
    const baseColumns = [
        {
            field: 'devices',
            headerName: t.popup?.labelDevice || t.column.device || 'Devices',
            width: 320,
            align: 'left',
            headerAlign: 'left',
            editable: false,
            valueGetter: (_value, row) => resolveDevicesLabel(row),
        },
        { field: 'name', headerName: t.column.name, width: 200, align: 'left', headerAlign: 'left', editable: false },
        { field: 'address', headerName: t.column.address, width: 500, align: 'left', headerAlign: 'left', editable: false },
        { field: 'latitude', headerName: t.column.latitude, width: 200, align: 'center', headerAlign: 'center', editable: false },
        { field: 'longitude', headerName: t.column.longitude, width: 200, align: 'center', headerAlign: 'center', editable: false },
    ];

    const columns = canPerformActions
        ? [
            {
                field: 'actions', type: 'actions', headerName: t.column.actions, width: 100,
                cellClassName: 'actions',
                getActions: (params) => {
                    const actionItems = [];
                    actionItems.push(
                        <GridActionsCellItem
                            icon={<VisibilityOutlinedIcon />}
                            label={t.actions?.view || 'View'}
                            showInMenu
                            onClick={() => openLocationDetails(params.row)}
                        />
                    );
                    if (canEdit) {
                        actionItems.push(
                            <GridActionsCellItem
                                icon={<EditIcon />}
                                label={t.actions.edit}
                                showInMenu
                                onClick={() => openEditLocationModal(params.row)}
                            />
                        );
                    }
                    if (canDelete) {
                        actionItems.push(
                            <GridActionsCellItem
                                icon={<DeleteIcon style={{ color: 'red' }} />}
                                label={t.actions.delete}
                                showInMenu
                                onClick={handleDeleteClick(params.id)}
                            />
                        );
                    }
                    return actionItems;
                },
            },
            ...baseColumns,
        ]
        : baseColumns;

    const fetchData = React.useCallback(async () => {
        if (!canView) {
            setLoading(false);
            setRows([]);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(datasource);
            if (res.data && Array.isArray(res.data.data)) {
                setRows(res.data.data.map(normalizeLocationListRow));
            } else {
                console.error("Received unexpected data format from /location:", res.data);
                setRows([]);
                await showAlert('error', 'Received an invalid format from the server.');
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            await showAlert('error', errorMessage);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [datasource, canView, showAlert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const [selectedRows, setSelectedRows] = useState([]);

    const selectActions = canDelete
        ? [{
            title: t.actions.delete,
            icon: <DeleteIcon style={{ color: 'rgb(236, 69, 78)', marginInlineEnd: '20px' }} />,
            action: async (ids) => {
                if (!canDelete) {
                    await showAlert('error', t.noPermission);
                    return;
                }
                await deleteMultiLocations(ids, fetchData, alert);
                setSelectedRows([]);
            }
        }]
        : [];

    if (!canView) {
        return <div style={{ padding: '20px' }}>{t.noPermission}</div>;
    }

    return (
        <div
            className="configuration devices-page"
            style={{ display: "flex", flexDirection: "column", height: "100%"}}
            dir={isRtl ? "rtl" : "ltr"}
        >
            <AppDialog
                open={dialog.open}
                type={dialog.type}
                variant={dialog.variant}
                icon={dialog.icon}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                showCancel={dialog.showCancel}
                onConfirm={() => closeDialog(true)}
                onCancel={() => closeDialog(false)}
                onClose={() => closeDialog(false)}
            />

            {canCreate && addLocationOpen && (
                <AddLocation
                    refresh={fetchData}
                    setState={setAddLocationOpen}
                    devices={devices}
                    devicesLoading={devicesLoading}
                />
            )}
            {canEdit && editLocationOpen && editingLocation && (
                <EditLocation
                    location={editingLocation}
                    refresh={fetchData}
                    setState={setEditLocationOpen}
                    devices={devices}
                    devicesLoading={devicesLoading}
                />
            )}
            <LocationDetailsModal state={viewingLocation} setState={setViewingLocation} />

            <div className="device-main">
                <div className="devices-header">
                    <div className="devices-header-left">
                        <h2 className="devices-title" >
                            {t.locationsPageTitle}
                        </h2>
                    </div>

                    {canCreate && (
                        <button
                            className="devices-primary-btn"
                            onClick={openAddLocationModal}
                        >
                            <span>{t.newLocation}</span>
                        </button>
                    )}
                </div>

                {loading ?
                    <Spinner />
                    : rows && rows.length > 0 ?
                        <div
                            className="table-wrapper"
                            style={{
                                flex: 1,
                                minHeight: 0,
                                marginTop: '20px'
                            }}
                        >
                            <DataGridTable
                                rows={rows}
                                columns={columns}
                                idcolumn={'id'}
                                pagingoptions={pagingoptions}
                                checkboxSelection={true}
                                rowSelectionModel={selectedRows}
                                setRowSelectionModel={setSelectedRows}
                                selectActions={selectActions}
                                showToolbar={true}
                                tableKey='locations'
                                handleRowClick={(params) => {
                                    const field = params?.field || '';
                                    if (field === 'actions' || field === '__check__') return;
                                    openLocationDetails(params?.row);
                                }}
                            />
                        </div>
                        : <div style={{
                            width: '100%',
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            {t.noData}
                        </div>
                }
            </div>
        </div>
    );
};
